import type { IndicatorCategory, IndicatorComparator, ObjectiveStatus } from './types';

export const OBJECTIVE_STATUS_LABEL: Record<ObjectiveStatus, string> = {
  borrador: 'Borrador',
  activo: 'Activo',
  logrado: 'Logrado',
  no_logrado: 'No logrado',
  cancelado: 'Cancelado',
};

export const COMPARATOR_LABEL: Record<IndicatorComparator, string> = {
  gte: '≥ meta',
  lte: '≤ meta',
  eq: '= meta',
  between: 'Entre límites',
};

export const FREQUENCY_LABEL: Record<string, string> = {
  mensual: 'Mensual',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
};

export const INDICATOR_CATEGORY_LABEL: Record<IndicatorCategory, string> = {
  calidad: 'Calidad',
  ambiental: 'Ambiental',
};

export function resolveIndicatorCategory(category: IndicatorCategory | null | undefined): IndicatorCategory {
  return category ?? 'calidad';
}

export function withinTargetBadgeClass(withinTarget: boolean): string {
  return withinTarget
    ? 'bg-green-100 text-green-800'
    : 'bg-red-100 text-red-800';
}

export function withinTargetLabel(withinTarget: boolean): string {
  return withinTarget ? 'En meta' : 'Fuera de meta';
}

export function progressBarClass(percent: number): string {
  if (percent >= 100) return 'bg-green-500';
  if (percent >= 50) return 'bg-blue-500';
  return 'bg-amber-500';
}

/** Serializa rango de fechas para periodLabel (máx. 40 caracteres en BD). */
export function formatMeasurementPeriodLabel(start: string, end: string): string {
  return `${start}/${end}`;
}

/** Etiqueta legible para gráficos y listados (soporta rango o etiqueta libre legacy). */
export function formatMeasurementPeriodDisplay(periodLabel: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})\/(\d{4}-\d{2}-\d{2})$/.exec(periodLabel);
  if (!match) return periodLabel;
  const [, start, end] = match;
  if (start === end) return start;
  return `${start} — ${end}`;
}

const FORMULA_KEYWORDS = new Set([
  'IF',
  'MIN',
  'MAX',
  'SUM',
  'AVG',
  'ROUND',
  'CONCAT',
  'LEN',
  'NOW',
  'TODAY',
  'DAYS',
  'IFEMPTY',
  'COALESCE',
  'field',
  'LOOKUP',
  'true',
  'false',
  'null',
]);

/** Variables referenciadas en una fórmula de indicador (${x}, field("x") o identificadores). */
export function extractFormulaVariables(formula: string): string[] {
  const ids = new Set<string>();
  const refPattern = /\$\{([^}]+)\}/g;
  let match: RegExpExecArray | null;
  while ((match = refPattern.exec(formula)) !== null) {
    ids.add(match[1].trim());
  }
  const fieldPattern = /field\s*\(\s*["']([^"']+)["']\s*\)/gi;
  while ((match = fieldPattern.exec(formula)) !== null) {
    ids.add(match[1].trim());
  }
  const withoutStrings = formula.replace(/"[^"]*"|'[^']*'/g, ' ');
  const tokenPattern = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g;
  while ((match = tokenPattern.exec(withoutStrings)) !== null) {
    if (!FORMULA_KEYWORDS.has(match[1])) {
      ids.add(match[1]);
    }
  }
  return [...ids];
}
